import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "position-detail-container" }
const _hoisted_2 = {
  key: 0,
  class: "loading-container"
}
const _hoisted_3 = {
  key: 1,
  class: "not-found-container"
}
const _hoisted_4 = {
  key: 2,
  class: "position-content"
}
const _hoisted_5 = { class: "card-header" }
const _hoisted_6 = { class: "header-actions" }
const _hoisted_7 = { class: "position-info" }
const _hoisted_8 = { class: "info-row" }
const _hoisted_9 = { class: "info-item" }
const _hoisted_10 = { class: "value" }
const _hoisted_11 = { class: "info-item" }
const _hoisted_12 = { class: "value" }
const _hoisted_13 = { class: "info-row" }
const _hoisted_14 = { class: "info-item" }
const _hoisted_15 = {
  key: 0,
  class: "value"
}
const _hoisted_16 = {
  key: 1,
  class: "value"
}
const _hoisted_17 = { class: "info-item" }
const _hoisted_18 = { class: "value" }
const _hoisted_19 = { class: "info-row" }
const _hoisted_20 = { class: "info-item" }
const _hoisted_21 = { class: "value" }
const _hoisted_22 = { class: "info-item" }
const _hoisted_23 = { class: "value" }
const _hoisted_24 = {
  key: 0,
  class: "info-row"
}
const _hoisted_25 = {
  key: 0,
  class: "info-item"
}
const _hoisted_26 = { class: "value" }
const _hoisted_27 = {
  key: 1,
  class: "info-item"
}
const _hoisted_28 = { class: "value" }
const _hoisted_29 = {
  key: 0,
  class: "job-url-section"
}
const _hoisted_30 = {
  key: 1,
  class: "description-section"
}
const _hoisted_31 = { class: "content-box" }
const _hoisted_32 = {
  key: 2,
  class: "requirements-section"
}
const _hoisted_33 = { class: "content-box" }

import { ref, onMounted, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Edit, Delete, Link } from '@element-plus/icons-vue';
import { usePositionStore } from '@/store/positionStore';
import type { Position } from '@/types/position';
import { WORK_TYPE_OPTIONS, PRIORITY_OPTIONS } from '@/types/position';
import ApplicationStatusTag from '@/components/position/ApplicationStatusTag.vue';
import InterviewStatusTag from '@/components/position/InterviewStatusTag.vue';
import PositionForm from '@/components/position/PositionForm.vue';
import InterviewList from '@/components/interview/InterviewList.vue';
import ResourceList from '@/components/resource/ResourceList.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'PositionDetailView',
  setup(__props) {

const router = useRouter();
const route = useRoute();
const positionStore = usePositionStore();

const position = ref<Position>({} as Position);
const loading = ref(true);
const dialogVisible = ref(false);

const positionId = route.params.id as string;

onMounted(async () => {
  if (positionId) {
    await loadPosition();
    
    // 如果URL中有#interviews锚点，滚动到面试列表
    if (window.location.hash === '#interviews') {
      nextTick(() => {
        const element = document.getElementById('interviews');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      });
    }
  } else {
    loading.value = false;
  }
});

const loadPosition = async () => {
  try {
    loading.value = true;
    await positionStore.getPosition(Number(positionId));
    position.value = positionStore.currentPosition || {} as Position;
  } catch (error) {
    ElMessage.error('加载职位详情失败');
    console.error(error);
  } finally {
    loading.value = false;
  }
};

const goBack = () => {
  if (position.value?.company?.id) {
    router.push(`/resources/companies/${position.value.company.id}`);
  } else {
    router.push('/resources/positions');
  }
};

const handleEdit = () => {
  dialogVisible.value = true;
};

const confirmDelete = () => {
  ElMessageBox.confirm(
    '确定要删除这个职位记录吗？此操作无法恢复。',
    '确认删除',
    {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning',
    }
  )
    .then(() => {
      handleDeletePosition();
    })
    .catch(() => {
      // 用户取消删除
    });
};

const handleDeletePosition = async () => {
  try {
    await positionStore.deletePosition(Number(positionId));
    ElMessage.success('职位删除成功');
    router.push('/resources/positions');
  } catch (error: any) {
    ElMessage.error(`删除职位失败: ${error.message || '未知错误'}`);
    console.error(error);
  }
};

const handleFormSubmit = async (updatedPosition: Partial<Position>) => {
  try {
    if (updatedPosition.id) {
      await positionStore.updatePosition(updatedPosition);
      ElMessage.success('职位更新成功');
      await loadPosition();
      dialogVisible.value = false;
    }
  } catch (error) {
    ElMessage.error('更新职位失败');
    console.error(error);
  }
};

// 当面试列表发生变化时刷新职位信息
const handleInterviewsRefresh = async () => {
  await loadPosition();
};

const handleResourceRefresh = async () => {
  await loadPosition();
};

const getWorkTypeLabel = (type: string) => {
  const option = WORK_TYPE_OPTIONS.find(opt => opt.value === type);
  return option ? option.label : type;
};

const getPriorityLabel = (priority: number) => {
  const option = PRIORITY_OPTIONS.find(opt => opt.value === priority);
  return option ? option.label : priority;
};

const getPriorityType = (priority: number) => {
  if (priority >= 4) return 'danger';
  if (priority >= 3) return 'warning';
  if (priority >= 2) return 'success';
  return 'info';
};

const formatDate = (dateString: string) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  return date.toLocaleDateString('zh-CN', { 
    year: 'numeric', 
    month: '2-digit', 
    day: '2-digit' 
  });
};

return (_ctx: any,_cache: any) => {
  const _component_el_page_header = _resolveComponent("el-page-header")!
  const _component_el_skeleton = _resolveComponent("el-skeleton")!
  const _component_el_empty = _resolveComponent("el-empty")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_link = _resolveComponent("el-link")!
  const _component_el_card = _resolveComponent("el-card")!
  const _component_el_dialog = _resolveComponent("el-dialog")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_el_page_header, {
      onBack: goBack,
      title: '职位详情',
      class: "page-header"
    }),
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(_component_el_skeleton, {
            rows: 10,
            animated: ""
          })
        ]))
      : (!position.value.id)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(_component_el_empty, { description: "未找到职位信息" }),
            _createVNode(_component_el_button, {
              onClick: goBack,
              type: "primary"
            }, {
              default: _withCtx(() => _cache[2] || (_cache[2] = [
                _createTextVNode("返回职位列表")
              ])),
              _: 1
            })
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createVNode(_component_el_card, { class: "position-card" }, {
              header: _withCtx(() => [
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("h2", null, _toDisplayString(position.value.title), 1),
                  _createElementVNode("div", _hoisted_6, [
                    _createVNode(_component_el_button, {
                      type: "primary",
                      onClick: handleEdit,
                      size: "small",
                      icon: _unref(Edit)
                    }, {
                      default: _withCtx(() => _cache[3] || (_cache[3] = [
                        _createTextVNode(" 编辑 ")
                      ])),
                      _: 1
                    }, 8, ["icon"]),
                    _createVNode(_component_el_button, {
                      type: "danger",
                      onClick: confirmDelete,
                      size: "small",
                      icon: _unref(Delete)
                    }, {
                      default: _withCtx(() => _cache[4] || (_cache[4] = [
                        _createTextVNode(" 删除 ")
                      ])),
                      _: 1
                    }, 8, ["icon"])
                  ])
                ])
              ]),
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("div", _hoisted_8, [
                    _createElementVNode("div", _hoisted_9, [
                      _cache[5] || (_cache[5] = _createElementVNode("span", { class: "label" }, "公司", -1)),
                      _createElementVNode("span", _hoisted_10, _toDisplayString(position.value.company?.name || '未知'), 1)
                    ]),
                    _createElementVNode("div", _hoisted_11, [
                      _cache[6] || (_cache[6] = _createElementVNode("span", { class: "label" }, "工作类型", -1)),
                      _createElementVNode("span", _hoisted_12, _toDisplayString(getWorkTypeLabel(position.value.workType)), 1)
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_13, [
                    _createElementVNode("div", _hoisted_14, [
                      _cache[7] || (_cache[7] = _createElementVNode("span", { class: "label" }, "薪资范围", -1)),
                      (position.value.minSalary && position.value.maxSalary)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_15, _toDisplayString(position.value.minSalary) + "-" + _toDisplayString(position.value.maxSalary) + " " + _toDisplayString(position.value.salaryCurrency || 'CNY'), 1))
                        : (_openBlock(), _createElementBlock("span", _hoisted_16, "未设置"))
                    ]),
                    _createElementVNode("div", _hoisted_17, [
                      _cache[8] || (_cache[8] = _createElementVNode("span", { class: "label" }, "优先级", -1)),
                      _createElementVNode("span", _hoisted_18, [
                        _createVNode(_component_el_tag, {
                          type: getPriorityType(position.value.priority),
                          effect: "plain"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(getPriorityLabel(position.value.priority)), 1)
                          ]),
                          _: 1
                        }, 8, ["type"])
                      ])
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_19, [
                    _createElementVNode("div", _hoisted_20, [
                      _cache[9] || (_cache[9] = _createElementVNode("span", { class: "label" }, "申请状态", -1)),
                      _createElementVNode("span", _hoisted_21, [
                        _createVNode(ApplicationStatusTag, {
                          status: position.value.applicationStatus
                        }, null, 8, ["status"])
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_22, [
                      _cache[10] || (_cache[10] = _createElementVNode("span", { class: "label" }, "面试状态", -1)),
                      _createElementVNode("span", _hoisted_23, [
                        _createVNode(InterviewStatusTag, {
                          status: position.value.interviewStatus
                        }, null, 8, ["status"])
                      ])
                    ])
                  ]),
                  (position.value.deadline || position.value.nextInterviewDate)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_24, [
                        (position.value.deadline)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
                              _cache[11] || (_cache[11] = _createElementVNode("span", { class: "label" }, "截止日期", -1)),
                              _createElementVNode("span", _hoisted_26, _toDisplayString(formatDate(position.value.deadline)), 1)
                            ]))
                          : _createCommentVNode("", true),
                        (position.value.nextInterviewDate)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_27, [
                              _cache[12] || (_cache[12] = _createElementVNode("span", { class: "label" }, "下次面试", -1)),
                              _createElementVNode("span", _hoisted_28, _toDisplayString(formatDate(position.value.nextInterviewDate)), 1)
                            ]))
                          : _createCommentVNode("", true)
                      ]))
                    : _createCommentVNode("", true)
                ]),
                (position.value.jobUrl)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_29, [
                      _cache[13] || (_cache[13] = _createElementVNode("h3", null, "职位链接", -1)),
                      _createVNode(_component_el_link, {
                        href: position.value.jobUrl,
                        target: "_blank",
                        type: "primary"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(position.value.jobUrl) + " ", 1),
                          _createVNode(_component_el_icon, { class: "el-icon--right" }, {
                            default: _withCtx(() => [
                              _createVNode(_unref(Link))
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }, 8, ["href"])
                    ]))
                  : _createCommentVNode("", true),
                (position.value.description)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_30, [
                      _cache[14] || (_cache[14] = _createElementVNode("h3", null, "职位描述", -1)),
                      _createElementVNode("div", _hoisted_31, _toDisplayString(position.value.description), 1)
                    ]))
                  : _createCommentVNode("", true),
                (position.value.requirements)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_32, [
                      _cache[15] || (_cache[15] = _createElementVNode("h3", null, "职位要求", -1)),
                      _createElementVNode("div", _hoisted_33, _toDisplayString(position.value.requirements), 1)
                    ]))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }),
            _createVNode(_component_el_card, {
              class: "interviews-card",
              id: "interviews"
            }, {
              default: _withCtx(() => [
                _createVNode(InterviewList, {
                  "position-id": Number(_unref(positionId)),
                  onRefresh: handleInterviewsRefresh
                }, null, 8, ["position-id"])
              ]),
              _: 1
            }),
            _createVNode(ResourceList, {
              "position-id": position.value.id,
              onRefresh: handleResourceRefresh
            }, null, 8, ["position-id"])
          ])),
    _createVNode(_component_el_dialog, {
      modelValue: dialogVisible.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((dialogVisible).value = $event)),
      title: "编辑职位",
      width: "60%",
      "destroy-on-close": ""
    }, {
      default: _withCtx(() => [
        (dialogVisible.value)
          ? (_openBlock(), _createBlock(PositionForm, {
              key: 0,
              position: position.value,
              loading: loading.value,
              onSubmit: handleFormSubmit,
              onCancel: _cache[0] || (_cache[0] = ($event: any) => (dialogVisible.value = false))
            }, null, 8, ["position", "loading"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["modelValue"])
  ]))
}
}

})